import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import { longDate, shortDate } from '../format';
import { compressImage } from '../image';
import type { Inspection, InspectionItem, ItemState } from '../types';
import { SignaturePad } from './SignaturePad';

const STATE_OPTIONS: Array<{ value: ItemState; short: string; label: string }> = [
  { value: 'VERY_GOOD', short: 'TB', label: 'Très bon' },
  { value: 'GOOD', short: 'B', label: 'Bien' },
  { value: 'AVERAGE', short: 'M', label: 'Moyen' },
  { value: 'POOR', short: 'MM', label: 'Mauvais' },
];

const STATE_LABEL: Record<ItemState, string> = {
  VERY_GOOD: 'Très bon', GOOD: 'Bien', AVERAGE: 'Moyen', POOR: 'Mauvais',
};

function requiresPhoto(state: ItemState | null): boolean {
  return state === 'AVERAGE' || state === 'POOR';
}

export function Mission({ onLogout }: { onLogout: () => Promise<void> }) {
  const [inspection, setInspection] = useState<Inspection | null>(null);
  const [openRoom, setOpenRoom] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busyItems, setBusyItems] = useState<Set<string>>(new Set());
  const [uploading, setUploading] = useState<Set<string>>(new Set());
  const [finalizing, setFinalizing] = useState(false);
  // Visionneuse plein écran : sur téléphone, une vignette de 58 px ne permet
  // de vérifier ni une rayure ni une trace, et l'agrandissement natif demandait
  // un appui long que rien n'indiquait.
  const [zoomed, setZoomed] = useState<{ url: string; label: string } | null>(null);
  const metaVersion = useRef(1);
  const metaQueue = useRef(Promise.resolve());

  async function load(): Promise<void> {
    try {
      const result = await api<Inspection>('/api/mission/current');
      setInspection(result);
      metaVersion.current = result.version;
      const firstIncomplete = result.items.find((item) => item.state === null)?.roomLabel ?? result.items[0]?.roomLabel ?? '';
      setOpenRoom((current) => current || firstIncomplete);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Chargement impossible.');
    }
  }

  useEffect(() => { void load(); }, []);

  const groups = useMemo(() => {
    const map = new Map<string, InspectionItem[]>();
    for (const item of inspection?.items ?? []) {
      const list = map.get(item.roomLabel) ?? [];
      list.push(item);
      map.set(item.roomLabel, list);
    }
    return [...map.entries()];
  }, [inspection]);

  const progress = useMemo(() => {
    const items = inspection?.items ?? [];
    const completed = items.filter((item) => item.state !== null).length;
    return { completed, total: items.length, percent: items.length ? Math.round(completed / items.length * 100) : 0 };
  }, [inspection]);

  function updateLocalItem(itemId: string, change: Partial<InspectionItem>): void {
    setInspection((current) => current ? {
      ...current,
      items: current.items.map((item) => item.id === itemId ? { ...item, ...change } : item),
    } : current);
  }

  async function saveItem(item: InspectionItem, change: Partial<InspectionItem>): Promise<void> {
    if (busyItems.has(item.id) || inspection?.status !== 'DRAFT') return;
    const next = { ...item, ...change };
    updateLocalItem(item.id, change);
    setBusyItems((current) => new Set(current).add(item.id));
    setError('');
    try {
      const result = await api<{ version: number }>(`/api/mission/items/${item.id}`, {
        method: 'PATCH',
        // Seuls les champs modifiés partent : un élément pas encore noté n'a
        // pas d'état à envoyer, et l'exiger faisait échouer la requête.
        // ⚠️ Ces champs se lisent sur `next`, jamais sur `change`.
        //
        // Le commentaire était auparavant conditionné à `change.comment`. Or la
        // zone de saisie appelle `saveItem({ ...item, comment }, {})` : le
        // commentaire arrive dans le premier argument, `change` est vide, et la
        // condition était donc toujours fausse. Le texte restait dans l'état
        // React (donc parfaitement visible à l'écran pendant tout le constat)
        // et ne partait jamais au serveur. Il disparaissait au moment où le PDF
        // était produit depuis la base. Aucune réserve écrite n'a jamais été
        // enregistrée jusqu'ici (incident du 22/08/2026, constat d'entrée
        // d'Aurélien : sol dégradé et dangereux, moquette de balcon décollée).
        //
        // Un état des lieux sans ses réserves écrites ne vaut rien au moment
        // d'une retenue : ce champ doit partir à chaque enregistrement.
        body: JSON.stringify({
          version: item.version,
          ...(next.state ? { state: next.state } : {}),
          comment: next.comment ?? '',
          ...(next.attributable !== undefined ? { attributable: next.attributable } : {}),
        }),
      });
      updateLocalItem(item.id, { version: result.version });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Enregistrement impossible.');
      await load();
    } finally {
      setBusyItems((current) => { const nextSet = new Set(current); nextSet.delete(item.id); return nextSet; });
    }
  }

  function setMetaLocal(change: Partial<Inspection>): void {
    setInspection((current) => current ? { ...current, ...change } : current);
  }

  function saveMeta(change: Partial<Inspection>): void {
    if (inspection?.status !== 'DRAFT') return;
    setMetaLocal(change);
    metaQueue.current = metaQueue.current.then(async () => {
      try {
        const result = await api<{ version: number }>('/api/mission/inspection', {
          method: 'PATCH',
          body: JSON.stringify({ version: metaVersion.current, ...change }),
        });
        metaVersion.current = result.version;
        setInspection((current) => current ? { ...current, version: result.version } : current);
        setNotice('Brouillon enregistré');
        window.setTimeout(() => setNotice(''), 1200);
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : 'Enregistrement impossible.');
      }
    });
  }

  async function addPhoto(item: InspectionItem, file: File): Promise<void> {
    setUploading((current) => new Set(current).add(item.id));
    setError('');
    try {
      const compressed = await compressImage(file);
      const form = new FormData();
      form.append('photo', compressed, `photo-${Date.now()}.jpg`);
      const photo = await api<InspectionItem['photos'][number]>(`/api/mission/items/${item.id}/photos`, { method: 'POST', body: form });
      updateLocalItem(item.id, { photos: [...item.photos, photo] });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Envoi de la photo impossible.');
    } finally {
      setUploading((current) => { const next = new Set(current); next.delete(item.id); return next; });
    }
  }

  async function removePhoto(item: InspectionItem, photoId: string): Promise<void> {
    await api(`/api/mission/photos/${photoId}`, { method: 'DELETE' });
    updateLocalItem(item.id, { photos: item.photos.filter((photo) => photo.id !== photoId) });
  }

  async function saveSignature(role: 'REPRESENTATIVE' | 'TENANT', signerName: string, dataUrl: string): Promise<void> {
    await api(`/api/mission/signatures/${role}`, {
      method: 'PUT',
      body: JSON.stringify({ signerName, dataUrl }),
    });
    setInspection((current) => current ? {
      ...current,
      signatures: [...current.signatures.filter((signature) => signature.role !== role), { role, signerName, signedAt: new Date().toISOString() }],
      ...(role === 'REPRESENTATIVE' ? { representativeName: signerName } : {}),
    } : current);
  }

  async function finalize(): Promise<void> {
    // Une réserve saisie juste avant de clôturer part au serveur à la sortie du
    // champ ; clôturer dans la foulée pourrait produire le PDF avant que cet
    // envoi ne soit arrivé, et la réserve manquerait au document signé.
    if (busyItems.size > 0) {
      setError('Enregistrement en cours, réessayez dans un instant.');
      return;
    }
    setFinalizing(true);
    setError('');
    await metaQueue.current;
    try {
      const result = await api<Inspection>('/api/mission/finalize', { method: 'POST' });
      setInspection(result);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Finalisation impossible.');
    } finally {
      setFinalizing(false);
    }
  }

  if (!inspection) {
    return <main className="center-page">{error ? <div className="message-card error-card"><h1>Mission indisponible</h1><p>{error}</p></div> : <div className="loading-mark"><span /><span /><span /></div>}</main>;
  }

  const locked = inspection.status !== 'DRAFT';
  const missingPhotos = inspection.items.filter((item) => requiresPhoto(item.state) && item.photos.length === 0).length;
  const representativeSignature = inspection.signatures.find((signature) => signature.role === 'REPRESENTATIVE');
  const tenantSignature = inspection.signatures.find((signature) => signature.role === 'TENANT');

  return (
    <div className="mission-shell">
      {zoomed && (
        <div
          className="photo-viewer"
          role="dialog"
          aria-modal="true"
          aria-label={zoomed.label}
          tabIndex={-1}
          ref={(node) => node?.focus()}
          onClick={() => setZoomed(null)}
          onKeyDown={(event) => { if (event.key === 'Escape') setZoomed(null); }}
        >
          <button type="button" className="photo-viewer-close" aria-label="Fermer">×</button>
          <img src={zoomed.url} alt={zoomed.label} onClick={(event) => event.stopPropagation()} />
          <p>{zoomed.label}</p>
        </div>
      )}
      <header className="mission-header">
        <div className="mission-header-main">
          <div><p className="eyebrow">Mission temporaire</p><h1>{inspection.type === 'ENTRY' ? 'État des lieux d’entrée' : 'État des lieux de sortie'}</h1></div>
          <button className="text-button light" onClick={() => void onLogout()}>Quitter</button>
        </div>
        <div className="mission-context">
          <strong>{inspection.room ? `Chambre ${inspection.room.name}` : 'Parties communes'}</strong><span>{inspection.tenantName}</span><span>{longDate(inspection.scheduledFor)}</span>
        </div>
        <div className="progress-line"><span style={{ width: `${progress.percent}%` }} /></div>
      </header>

      {inspection.status === 'SIGNED' && (
        <section className="completion-banner">
          <div className="completion-check">✓</div>
          <div><p className="eyebrow">Mission terminée</p><h2>Le constat est signé et verrouillé.</h2><p>Le PDF final est conservé avec ses photos et signatures.</p></div>
          {inspection.pdfUrl && <a className="button primary" href={inspection.pdfUrl} target="_blank" rel="noreferrer">Ouvrir le PDF</a>}
        </section>
      )}

      <nav className="room-rail" aria-label="Progression par pièce">
        {groups.map(([room, items], index) => {
          const done = items.filter((item) => item.state !== null).length;
          return <button key={room} className={openRoom === room ? 'active' : ''} onClick={() => { setOpenRoom(room); document.getElementById(`room-${index}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}><span>{room}</span><b>{done}/{items.length}</b></button>;
        })}
      </nav>

      <main className="mission-body">
        <section className="field-card">
          <div className="section-heading"><div><p className="eyebrow">Avant de commencer</p><h2>Repères du constat</h2></div><span className="autosave">{notice || 'Sauvegarde automatique'}</span></div>
          <div className="form-grid meta-grid">
            <label>Intervenant / mandataire<input disabled={locked} value={inspection.representativeName} onChange={(event) => setMetaLocal({ representativeName: event.target.value })} onBlur={(event) => saveMeta({ representativeName: event.target.value })} placeholder="Prénom et nom" /></label>
            <label>Locataire<input disabled={locked} value={inspection.tenantName} onChange={(event) => setMetaLocal({ tenantName: event.target.value })} onBlur={(event) => saveMeta({ tenantName: event.target.value })} /></label>
            {inspection.type === 'EXIT' && <label className="span-2">Nouvelle adresse du locataire<input disabled={locked} value={inspection.tenantNewAddress} onChange={(event) => setMetaLocal({ tenantNewAddress: event.target.value })} onBlur={(event) => saveMeta({ tenantNewAddress: event.target.value })} /></label>}
          </div>
          {/* Les compteurs sont communs aux quatre colocataires : les relever à
              chaque constat de chambre n'apporte rien. Repliés par défaut,
              disponibles quand ils servent réellement (constat des communs,
              changement de fournisseur, litige). */}
          <details className="meters-field">
            <summary>Relevés de compteurs (facultatif, communs au logement)</summary>
            <div className="form-grid meta-grid">
            <label>N° compteur électrique<input disabled={locked} value={inspection.electricityMeterNumber} onChange={(event) => setMetaLocal({ electricityMeterNumber: event.target.value })} onBlur={(event) => saveMeta({ electricityMeterNumber: event.target.value })} /></label>
            <label>Électricité HP<input disabled={locked} inputMode="decimal" value={inspection.electricityPeak} onChange={(event) => setMetaLocal({ electricityPeak: event.target.value })} onBlur={(event) => saveMeta({ electricityPeak: event.target.value })} /></label>
            <label>Électricité HC<input disabled={locked} inputMode="decimal" value={inspection.electricityOffPeak} onChange={(event) => setMetaLocal({ electricityOffPeak: event.target.value })} onBlur={(event) => saveMeta({ electricityOffPeak: event.target.value })} /></label>
            <label>Eau froide<input disabled={locked} inputMode="decimal" value={inspection.coldWater} onChange={(event) => setMetaLocal({ coldWater: event.target.value })} onBlur={(event) => saveMeta({ coldWater: event.target.value })} /></label>
            <label>Eau chaude<input disabled={locked} inputMode="decimal" value={inspection.hotWater} onChange={(event) => setMetaLocal({ hotWater: event.target.value })} onBlur={(event) => saveMeta({ hotWater: event.target.value })} /></label>
            </div>
          </details>
          <div className="form-grid meta-grid">
            {inspection.type === 'ENTRY' && <label className="check-field"><input disabled={locked} type="checkbox" checked={inspection.insuranceConfirmed} onChange={(event) => saveMeta({ insuranceConfirmed: event.target.checked })} /><span>Attestation d’assurance présentée</span></label>}
          </div>
          <fieldset className="keys-field"><legend>Clés remises ou reprises</legend><div className="key-grid">{inspection.availableKeys.map((key) => <label key={key}><input disabled={locked} type="checkbox" checked={inspection.keys.includes(key)} onChange={() => saveMeta({ keys: inspection.keys.includes(key) ? inspection.keys.filter((value) => value !== key) : [...inspection.keys, key] })} /><span>{key}</span></label>)}</div></fieldset>
        </section>

        <section className="inspection-list">
          <div className="list-intro"><div><p className="eyebrow">Constat pièce par pièce</p><h2>{progress.completed} éléments vérifiés sur {progress.total}</h2></div><strong>{progress.percent}%</strong></div>
          {groups.map(([room, items], groupIndex) => {
            const done = items.filter((item) => item.state !== null).length;
            // Une photo manquante se cherchait à l'aveugle : le total général
            // annonçait « 1 élément dégradé sans photo » sans dire où.
            const missingPhotos = items.filter(
              (item) => requiresPhoto(item.state) && item.photos.length === 0,
            ).length;
            const isOpen = openRoom === room;
            return (
              <article className={`room-section ${isOpen ? 'open' : ''}`} key={room} id={`room-${groupIndex}`}>
                <button className="room-heading" onClick={() => setOpenRoom(isOpen ? '' : room)} aria-expanded={isOpen}>
                  <span className="room-index">{String(groupIndex + 1).padStart(2, '0')}</span>
                  <span className="room-name"><strong>{room}</strong><small>{items[0]?.perimeter === 'COMMON' ? 'Partie commune · non imputable' : 'Partie privative'}</small></span>
                  <span className="room-count">
                    {done}/{items.length}
                    {missingPhotos > 0 && (
                      <em className="missing-photos" title="Éléments dégradés sans photo">
                        {missingPhotos} photo{missingPhotos > 1 ? 's' : ''} manquante{missingPhotos > 1 ? 's' : ''}
                      </em>
                    )}
                  </span><span className="chevron">›</span>
                </button>
                {isOpen && <div className="room-items">{items.map((item) => (
                  <section className={`inspection-item state-${item.state?.toLowerCase() ?? 'empty'}`} key={item.id}>
                    <div className="item-heading">
                      <div>
                        <h3>{item.itemLabel}</h3>
                        {item.referenceState && (
                          <p className="reference">
                            Avant : {STATE_LABEL[item.referenceState]}{item.referenceComment ? ` · ${item.referenceComment}` : ''}
                            {/* La photo du dégât précédent : sans elle, impossible de
                                comparer sur place et de dire si c'est le même défaut. */}
                            {item.referencePhotoUrl && (
                              <a href={item.referencePhotoUrl} target="_blank" rel="noreferrer">voir la photo d'avant</a>
                            )}
                          </p>
                        )}
                      </div>
                      {busyItems.has(item.id) && <span className="saving-dot">Sauvegarde…</span>}
                    </div>
                    {item.history.length > 0 && (
                      <details className="item-history">
                        <summary>
                          Historique ({item.history.length})
                          {item.history.some((event) => event.kind === 'REPAIR' && !event.reverted) && (
                            <em className="repaired-flag">réparé depuis</em>
                          )}
                        </summary>
                        <p className="history-summary">{item.historySummary}</p>
                        <ol className="history-list">
                          {item.history.map((event, index) => (
                            <li key={`${event.date}-${index}`} className={event.kind === 'REPAIR' ? 'repair' : ''}>
                              <time>{shortDate(event.date)}</time>
                              <span>
                                {event.kind === 'OBSERVATION'
                                  ? `Constat ${event.inspectionType === 'ENTRY' ? "d'entrée" : 'de sortie'} : `
                                    + `${STATE_LABEL[event.state]}${event.comment ? ` — ${event.comment}` : ''}`
                                  : `Réparation${event.performedBy ? ` par ${event.performedBy}` : ''}`
                                    + `${event.description ? ` : ${event.description}` : ''}`
                                    + `${event.reverted ? ' (déclaration annulée)' : ''}`}
                              </span>
                              <span className="history-photos">
                                {event.photoUrls.map((url, photoIndex) => (
                                  <button
                                    type="button"
                                    key={url}
                                    onClick={() => setZoomed({ url, label: `${item.itemLabel} · ${shortDate(event.date)}` })}
                                  >
                                    photo {photoIndex + 1}
                                  </button>
                                ))}
                              </span>
                            </li>
                          ))}
                        </ol>
                      </details>
                    )}
                    <div className="state-buttons" role="group" aria-label={`État de ${item.itemLabel}`}>{STATE_OPTIONS.map((option) => <button disabled={locked || busyItems.has(item.id)} className={item.state === option.value ? 'active' : ''} data-state={option.value} title={option.label} key={option.value} onClick={() => void saveItem(item, { state: option.value })}><b>{option.short}</b><span>{option.label}</span></button>)}</div>
                    <textarea disabled={locked} value={item.comment} onChange={(event) => updateLocalItem(item.id, { comment: event.target.value })} onBlur={(event) => void saveItem({ ...item, comment: event.target.value }, {})} placeholder="Observation utile, défaut, emplacement précis…" rows={2} />
                    <div className="photo-row">
                      {item.photos.map((photo) => (
                        <figure key={photo.id}>
                          <button
                            type="button"
                            className="photo-open"
                            aria-label={`Agrandir la photo ${photo.position} de ${item.itemLabel}`}
                            onClick={() => setZoomed({ url: photo.url, label: `${item.itemLabel} · photo ${photo.position}` })}
                          >
                            <img src={photo.url} alt={`${item.itemLabel}, photo ${photo.position}`} />
                          </button>
                          <button disabled={locked} aria-label="Supprimer la photo" onClick={() => void removePhoto(item, photo.id)}>×</button>
                        </figure>
                      ))}
                      {/* Deux vignettes : l'appareil photo d'un geste, indispensable
                          quand on parcourt 91 éléments sur place, et l'import pour
                          les clichés déjà pris. Un champ unique ne garantit ni l'un
                          ni l'autre selon le téléphone. */}
                      {!locked && <label className={`photo-button ${uploading.has(item.id) ? 'busy' : ''}`}><input type="file" accept="image/*" capture="environment" disabled={uploading.has(item.id)} onChange={(event) => { const file = event.target.files?.[0]; if (file) void addPhoto(item, file); event.target.value = ''; }} /><span>{uploading.has(item.id) ? '…' : '+'}</span><small>Photo</small></label>}
                      {/* Un fichier à la fois, volontairement : `addPhoto` compose la
                          nouvelle liste à partir de `item.photos` capturé à l'appel,
                          donc des envois simultanés s'écraseraient entre eux et une
                          seule vignette apparaîtrait, alors que le serveur les aurait
                          toutes reçues. */}
                      {!locked && <label className={`photo-button import ${uploading.has(item.id) ? 'busy' : ''}`}><input type="file" accept="image/*" disabled={uploading.has(item.id)} onChange={(event) => { const file = event.target.files?.[0]; if (file) void addPhoto(item, file); event.target.value = ''; }} /><span>⇪</span><small>Importer</small></label>}
                    </div>
                    {requiresPhoto(item.state) && item.photos.length === 0 && <p className="photo-required">Une photo est attendue pour cet état.</p>}
                    {/* L'état des lieux reste un simple constat : imputable ou non se décide ici
                        (question de fait, liée à ce qui vient d'être observé), mais le chiffrage —
                        coût de remplacement, vétusté, montant retenu — se fait après coup, à tête
                        reposée, dans le suivi Caution. */}
                    {item.perimeter === 'PRIVATE' && inspection.type === 'EXIT' && (
                      <div className="retention-row">
                        <p className="attribution-note">{item.attribution.reason}</p>
                        <label>
                          <input
                            disabled={locked || !item.attribution.allowed}
                            type="checkbox"
                            checked={item.attributable}
                            onChange={(event) => void saveItem(item, { attributable: event.target.checked })}
                          /> Imputable au locataire
                        </label>
                      </div>
                    )}
                  </section>
                ))}</div>}
              </article>
            );
          })}
        </section>

        <section className="field-card final-fields">
          <p className="eyebrow">Clôture</p><h2>Observations et signatures</h2>
          <label>Observations générales<textarea disabled={locked} value={inspection.generalComment} onChange={(event) => setMetaLocal({ generalComment: event.target.value })} onBlur={(event) => saveMeta({ generalComment: event.target.value })} rows={4} /></label>
          {missingPhotos > 0 && <label className="override-field">{missingPhotos} élément(s) dégradé(s) sans photo
            <textarea disabled={locked} value={inspection.photoOverrideReason} onChange={(event) => setMetaLocal({ photoOverrideReason: event.target.value })} onBlur={(event) => saveMeta({ photoOverrideReason: event.target.value })} placeholder="Si la photo est réellement impossible, expliquez précisément pourquoi…" rows={3} />
          </label>}
          {!locked && <div className="signature-grid">
            <SignaturePad title="Intervenant / mandataire" initialName={representativeSignature?.signerName || inspection.representativeName} saved={Boolean(representativeSignature)} onSave={(name, dataUrl) => saveSignature('REPRESENTATIVE', name, dataUrl)} />
            <SignaturePad title="Locataire" initialName={tenantSignature?.signerName || inspection.tenantName} saved={Boolean(tenantSignature)} onSave={(name, dataUrl) => saveSignature('TENANT', name, dataUrl)} />
          </div>}
        </section>
        {error && <div className="floating-error" role="alert"><strong>À vérifier</strong>{error}<button aria-label="Fermer" onClick={() => setError('')}>×</button></div>}
      </main>

      {!locked && <footer className="mission-footer"><div><strong>{progress.completed}/{progress.total}</strong><span>{missingPhotos ? `${missingPhotos} photo(s) attendue(s)` : 'Photos conformes'}</span></div><button className="button primary" disabled={finalizing} onClick={() => void finalize()}>{finalizing ? 'Création du PDF…' : 'Terminer et signer'}</button></footer>}
    </div>
  );
}

